import { memo, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import { ClassNames } from '@emotion/react'
import { Box, useTheme } from '@mui/material'
import PropTypes from 'prop-types'

import { useGetQuoteItemsQuery } from '@/app/services/quoteItems'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import GlbViewer from '@/common/components/GlbViewer/GlbViewer'
import PathTypeLegend from '@/common/components/PathTypeLegend/PathTypeLegend'
import ShortcutsLegend from '@/common/components/ShortcutsLegend/ShortcutsLegend'
import useSimpleKeyboardShortcut from '@/common/hooks/useSimpleKeyboardShortcut'

import DrawingRenderer from './Renderers/DrawingRenderer'
import ZoomControls from './ZoomControls'

const classes = {
    drawingContainer: {
        flex: '1 1 auto',
        boxSizing: 'border-box',
        position: 'relative',
    },

    selectArea: (theme) => ({
        borderWidth: '1px',
        borderStyle: 'dashed',
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.action.focus,
        zIndex: 1400,
    }),
}

const DrawingDoctor = ({
    drawing,
    onSelectPath,
    originalCadSvg,
    quoteItemId,
    selectedPaths,
    showCadView = false,
    showLegend = false,
    showShortcuts = false,
}) => {
    const params = useParams()
    const { quoteId } = params
    const theme = useTheme()

    const organisationId = useSelector(selectOrganisationId)
    const { quoteItem } = useGetQuoteItemsQuery(
        {
            organisationId,
            quoteId,
        },
        {
            selectFromResult: ({ data }) => ({
                quoteItem: data?.find((item) => item.id === quoteItemId),
            }),
        }
    )

    // Show the free three.js GLB viewer for the CAD view whenever the part has a
    // 3D model; otherwise fall back to the static PNG. We no longer use Forge.
    const canShow3dModel = Boolean(quoteItem?.glbModelUri)

    const showOpenPathCheckbox = useMemo(
        () => !!drawing?.issues?.find((issue) => issue.issueType === 'OpenInner' || issue.issueType === 'OpenOuter'),
        [drawing]
    )

    const viewBox = useMemo(() => {
        if (!drawing) {
            return { minX: 0, minY: 0, width: 0, height: 0 }
        }

        const next = {
            minX: drawing.viewBoxMinX !== 0 ? (drawing.viewBoxMinX ?? drawing.minX) : drawing.minX,
            minY: drawing.viewBoxMinY !== 0 ? (drawing.viewBoxMinY ?? drawing.minY) : drawing.minY,
            width: drawing.viewBoxWidth !== 0 ? (drawing.viewBoxWidth ?? drawing.width) : drawing.width,
            height: drawing.viewBoxHeight !== 0 ? (drawing.viewBoxHeight ?? drawing.height) : drawing.height,
        }

        // Apply a margin
        const viewBoxMargin = 0.02
        next.minX -= viewBoxMargin * next.width
        next.minY -= viewBoxMargin * next.height
        next.width *= 1 + 2 * viewBoxMargin
        next.height *= 1 + 2 * viewBoxMargin

        return next
    }, [drawing])

    const cadViewBox = useMemo(() => {
        if (!originalCadSvg) {
            return { minX: 0, minY: 0, width: 0, height: 0 }
        }

        const cad = new DOMParser().parseFromString(originalCadSvg, 'image/svg+xml')

        const next = {
            minX: cad.documentElement.viewBox.baseVal.x,
            minY: cad.documentElement.viewBox.baseVal.y,
            width: cad.documentElement.viewBox.baseVal.width,
            height: cad.documentElement.viewBox.baseVal.height,
        }

        // Apply a margin
        const viewBoxMargin = 0.01
        next.minX -= viewBoxMargin * next.width
        next.minY -= viewBoxMargin * next.height
        next.width *= 1 + 2 * viewBoxMargin
        next.height *= 1 + 2 * viewBoxMargin

        return next
    }, [originalCadSvg])

    const handlePathsSelected = (pathIds, added) => {
        if (added) {
            const result = pathIds.reduce(function (res, item) {
                if (!res[item]) {
                    res[item] = drawing.paths[item]
                } else {
                    // if we are adding paths, if the path already exists it gets deselected
                    delete res[item]
                }

                return res
            }, selectedPaths)
            onSelectPath((prev) => ({ ...prev, ...result }))
        } else {
            onSelectPath(Object.fromEntries(Object.entries(drawing.paths).filter(([k, _]) => pathIds.includes(k))))
        }
    }

    // Keyboard shortcuts for path selection
    useSimpleKeyboardShortcut('Escape', () => handlePathsSelected([], false))
    useSimpleKeyboardShortcut(['Modifier', 'a'], () => handlePathsSelected(Object.keys(drawing.paths), true))

    useEffect(() => {
        const handleContextMenu = (e) => {
            e.preventDefault()
        }

        const rootElement = document.querySelector('#drawing-doctor')

        // Not rendered while the 3D GLB viewer is shown (it returns early below).
        if (!rootElement) return undefined

        rootElement.addEventListener('contextmenu', handleContextMenu)

        return () => {
            rootElement.removeEventListener('contextmenu', handleContextMenu)
        }
    }, [])

    // The GLB viewer is a self-contained 3D canvas with its own OrbitControls, so
    // it must render outside the react-zoom-pan-pinch TransformWrapper (which throws
    // "Components are not mounted" when it has no TransformComponent child).
    if (showCadView && canShow3dModel) {
        return (
            <GlbViewer
                fallbackImageUri={quoteItem.modelViewUri}
                height="calc(100vh - 72px)"
                uri={quoteItem.glbModelUri}
            />
        )
    }

    return (
        <TransformWrapper
            centerZoomedOut={false}
            maxScale={16}
            minScale={0.4}
            panning={{
                allowLeftClickPan: false,
                allowMiddleClickPan: true,
                allowRightClickPan: false,
            }}
            wheel={{ smoothStep: 0.008 }}
            onPanningStart={() => {
                document.querySelector('.react-transform-wrapper').style.cursor = 'grabbing'
            }}
            onPanningStop={() => {
                document.querySelector('.react-transform-wrapper').style.cursor = 'crosshair'
            }}
        >
            {({ resetTransform, zoomIn, zoomOut }) => (
                <Box
                    id="drawing-doctor"
                    sx={classes.drawingContainer}
                    onAuxClick={(e) => {
                        if (e.button === 1 && e.detail === 2) {
                            resetTransform()
                        }
                    }}
                >
                    {showLegend ? <PathTypeLegend /> : null}

                    {showShortcuts ? <ShortcutsLegend /> : null}

                    <ZoomControls
                        onZoomIn={zoomIn}
                        onZoomOut={zoomOut}
                        onZoomReset={resetTransform}
                    />
                    {showCadView && quoteItem?.modelViewUri ? (
                        <TransformComponent
                            contentStyle={{
                                width: '100%',
                                height: '100%',
                            }}
                            wrapperStyle={{
                                width: '100%',
                                height: 'calc(100vh - 72px)',
                                cursor: 'crosshair',
                            }}
                        >
                            <Box
                                component="img"
                                src={quoteItem?.modelViewUri}
                                style={{
                                    width: '100%',
                                }}
                            />
                        </TransformComponent>
                    ) : (
                        <TransformComponent
                            contentStyle={{
                                width: '100%',
                                height: '100%',
                            }}
                            wrapperStyle={{
                                width: '100%',
                                height: 'calc(100vh - 72px)',
                                cursor: 'crosshair',
                            }}
                        >
                            <ClassNames>
                                {({ css }) => (
                                    <DrawingRenderer
                                        cadViewBox={cadViewBox}
                                        drawing={drawing}
                                        originalCadSvg={originalCadSvg}
                                        selectAreaClassName={css(classes.selectArea(theme))}
                                        selectedPaths={selectedPaths}
                                        showCad={showCadView}
                                        showOpenPathHighlight={showOpenPathCheckbox}
                                        viewBox={viewBox}
                                        onPathsSelected={handlePathsSelected}
                                    />
                                )}
                            </ClassNames>
                        </TransformComponent>
                    )}
                </Box>
            )}
        </TransformWrapper>
    )
}

DrawingDoctor.propTypes = {
    drawing: PropTypes.object,
    originalCadSvg: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    quoteItemId: PropTypes.string,
    selectedPaths: PropTypes.object,
    showCadView: PropTypes.bool,
    showLegend: PropTypes.bool,
    showShortcuts: PropTypes.bool,
    onSelectPath: PropTypes.func,
}

export default memo(DrawingDoctor)
